import { useHandler } from "@chatbotgang/etude/react/useHandler";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { SMTM_URL } from "@polifonia/env";
import type { TopUpStatus } from "@polifonia/interlude/billingConstants";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import urlJoin from "url-join";

import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";
import { useSmtmNavigation } from "@/features/billing/useSmtmNavigation";
import { useDateFormat } from "@/hooks/useDateFormat";
import { interlude } from "@/interlude";
import { TopupExpandedRow } from "@/pages/billing/v2/tabs/usageDetails/TopupExpandedRow";
import { TopupStatusTag } from "@/pages/billing/v2/tabs/usageDetails/TopupStatusTag";
import type { TopupFilters } from "@/pages/billing/v2/tabs/usageDetails/utils";
import { TOPUP_TABLE_COLUMN_COUNT } from "@/pages/billing/v2/tabs/usageDetails/utils";

const styles = {
  table: {
    tableLayout: "fixed",
  },
  headerCell: {
    color: theme.colors.neutral050,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
  },
  bodyRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  bodyCell: {
    borderBottom: `1px solid ${theme.colors.neutral010}`,
    verticalAlign: "middle",
  },
  expandCell: {
    borderBottom: `1px solid ${theme.colors.neutral010}`,
    verticalAlign: "middle",
    textAlign: "right",
    width: 48,
  },
  errorCell: {
    textAlign: "center",
    py: "48px",
  },
  loadingCell: {
    textAlign: "center",
    py: "48px",
  },
  topupLink: {
    color: theme.colors.colorBlue60,
    "&:hover": {
      textDecoration: "underline",
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const topupBaseUrl = SMTM_URL ? urlJoin(SMTM_URL, "/topup") : null;

const STATUSES_WITH_TOPUP_URL: Set<TopUpStatus> = new Set([
  "awaiting_payment",
  "pending",
]);

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

interface TopupTableProps {
  filters: TopupFilters;
}

export const TopupTable: React.FC<TopupTableProps> = function TopupTable({
  filters,
}) {
  const { t } = useTranslation();
  const dateFormat = useDateFormat({
    dateFormatOptions: DATE_FORMAT_OPTIONS,
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set(),
  );

  const navigateToSmtm = useSmtmNavigation();

  const topupsQuery = interlude.billing.useGetTopups({
    queries: {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      ...(filters.status && { status: filters.status }),
    },
  });

  const items = topupsQuery.data?.items ?? [];

  const handleToggleRow = useHandler((topupDisplayId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(topupDisplayId)) {
        next.delete(topupDisplayId);
      } else {
        next.add(topupDisplayId);
      }
      return next;
    });
  });

  return (
    <Box>
      <TableContainer>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCell}>
                {t("billing.topup.table.date")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.topup.table.topUpId")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.topup.table.status")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.topup.table.creditAmount")}
              </TableCell>
              <TableCell sx={styles.headerCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {topupsQuery.isPending ? (
              <TableRow>
                <TableCell
                  colSpan={TOPUP_TABLE_COLUMN_COUNT}
                  sx={styles.loadingCell}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : topupsQuery.isError ? (
              <TableRow>
                <TableCell
                  colSpan={TOPUP_TABLE_COLUMN_COUNT}
                  sx={styles.errorCell}
                >
                  <Typography variant="body">
                    {t("billing.error.generic")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isExpanded = expandedRows.has(item.topupDisplayId);
                return (
                  <Fragment key={item.topupDisplayId}>
                    <TableRow
                      sx={styles.bodyRow}
                      onClick={() => handleToggleRow(item.topupDisplayId)}
                    >
                      <TableCell sx={styles.bodyCell}>
                        <Typography variant="body">
                          {dateFormat(item.paidAt ?? item.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={styles.bodyCell}>
                        {STATUSES_WITH_TOPUP_URL.has(item.status) &&
                        topupBaseUrl ? (
                          <Box
                            component="a"
                            sx={styles.topupLink}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigateToSmtm(
                                urlJoin(topupBaseUrl, item.topupDisplayId),
                              );
                            }}
                          >
                            {item.topupDisplayId}
                          </Box>
                        ) : (
                          <Typography variant="body">
                            {item.topupDisplayId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={styles.bodyCell}>
                        <TopupStatusTag status={item.status} />
                      </TableCell>
                      <TableCell sx={styles.bodyCell}>
                        <NumberFormat
                          numberFormatPreset="currency"
                          numberFormatOptions={{ currency: item.currency }}
                          value={item.creditAmount}
                        />
                      </TableCell>
                      <TableCell sx={styles.expandCell}>
                        <IconButton size="small">
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TopupExpandedRow item={item} open={isExpanded} />
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
